import React from 'react';
import { marked } from 'marked';
import { FlagButton, renderExample } from '../PartDisplay';
import { X } from 'lucide-react';

/**
 * FINAL VERSION - Clean Blog Post Style with Dynamic Header & Signature Parsing
 * Extracts blog title, subtitle, date AND signature from mainText automatically
 * Handles both **bold** and *italic* markdown for headers
 */
const BlogPostComprehension = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleSingleAnswer,
  flaggedQuestions,
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet,
  ...props
}) => {
  // Parse blog content to extract header information and signature dynamically
  const parseBlogContent = () => {
    const mainText = partData.content?.mainText || partData.content?.text || '';
    const lines = mainText.split('\n').map(line => line.trim());

    // Extract first 3 header lines (bold OR italic)
    let blogTitle = '';
    let blogSubtitle = '';
    let blogDate = '';
    let contentStartIndex = 0;

    // Find and extract the header lines (can be ** or *)
    let headerLinesFound = 0;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check for bold (**text**) or italic (*text*)
      const isBold = line.startsWith('**') && line.endsWith('**') && line.length > 4;
      const isItalic = line.startsWith('*') && line.endsWith('*') && !line.startsWith('**') && line.length > 2;

      if (isBold || isItalic) {
        const cleanText = line.replace(/\*\*/g, '').replace(/\*/g, ''); // Remove all * markers

        if (headerLinesFound === 0) {
          blogTitle = cleanText;
        } else if (headerLinesFound === 1) {
          blogSubtitle = cleanText;
        } else if (headerLinesFound === 2) {
          blogDate = cleanText;
          contentStartIndex = i + 1;
          break;
        }
        headerLinesFound++;
      }
    }

    // Get content between headers and signature
    const contentLines = lines.slice(contentStartIndex);

    // Detect signature: last 1-2 non-empty lines that are short (likely a closing)
    let signatureLines = [];
    let contentEndIndex = contentLines.length;

    // Get last non-empty lines
    const nonEmptyLines = [];
    for (let i = contentLines.length - 1; i >= 0; i--) {
      if (contentLines[i].length > 0) {
        nonEmptyLines.unshift({ line: contentLines[i], index: i });
        if (nonEmptyLines.length >= 2) break; // Check last 2 lines max
      }
    }

    // Check if last line(s) look like a signature (short lines, < 50 chars each)
    if (nonEmptyLines.length >= 1) {
      const lastLines = nonEmptyLines.slice(-Math.min(2, nonEmptyLines.length));
      const allShort = lastLines.every(item => item.line.length < 50);
      const noMarkdown = lastLines.every(item => !item.line.startsWith('#') && !item.line.startsWith('**') && !item.line.startsWith('*'));

      // Additional check: signature often starts with common greetings
      const firstLineOfSignature = lastLines[0].line.toLowerCase();
      const signatureKeywords = ['bis bald', 'eure', 'euer', 'liebe grüße', 'viele grüße', 'gruß', 'lg', 'best', 'cheers'];
      const looksLikeSignature = signatureKeywords.some(keyword => firstLineOfSignature.includes(keyword));

      if (allShort && noMarkdown && looksLikeSignature) {
        signatureLines = lastLines.map(item => item.line);
        contentEndIndex = lastLines[0].index; // Cut content before signature
      }
    }

    // Get main content (without signature)
    const mainContent = contentLines.slice(0, contentEndIndex).join('\n');

    return {
      blogTitle,
      blogSubtitle,
      blogDate,
      mainContent,
      signatureLines
    };
  };

  const { blogTitle, blogSubtitle, blogDate, mainContent, signatureLines } = parseBlogContent();

  // Parse blog title to get the colored parts (e.g., "Susannes" "AlltagsBlog" ".at")
  const parseTitleParts = (title) => {
    // Match pattern like "SusannesAlltagsBlog.at" or similar
    const match = title.match(/^(.+?)(Alltags\w+)(\.at)$/i);
    if (match) {
      return {
        part1: match[1], // "Susannes"
        part2: match[2], // "AlltagsBlog"
        part3: match[3]  // ".at"
      };
    }
    // Fallback: return full title
    return { part1: title, part2: '', part3: '' };
  };

  const titleParts = parseTitleParts(blogTitle);

  // LEFT COLUMN CONTENT (The Reading Text)
  const blogContent = (
    <div className="space-y-6">
      {/* Blog Post Content */}
      <div className="bg-white p-8 shadow-sm">
        {/* Blog Header - Dynamic from JSON */}
        {blogTitle && (
          <div className="mb-8 pb-6 border-b border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              <span className="text-gray-900">{titleParts.part1}</span>
              <span className="text-gray-500">{titleParts.part2}</span>
              <span className="text-gray-900">{titleParts.part3}</span>
            </h1>
            {blogSubtitle && (
              <p className="text-sm text-gray-600 italic">
                {blogSubtitle}
              </p>
            )}
          </div>
        )}

        {/* Blog Post Date - Dynamic from JSON */}
        {blogDate && (
          <div className="text-sm text-gray-700 mb-6 font-medium italic">
            {blogDate}
          </div>
        )}

        {/* Main Blog Text - Content without headers and signature */}
        <div
          className="prose prose-base max-w-none text-gray-800 leading-relaxed"
          style={{
            fontSize: '15px',
            lineHeight: '1.7'
          }}
          dangerouslySetInnerHTML={{
            __html: marked.parse(mainContent, { breaks: true })
          }}
        />

        {/* Blog Signature - Dynamically detected */}
        {signatureLines.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            {signatureLines.map((line, idx) => (
              <p key={idx} className="text-sm text-gray-700">
                {line}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // RIGHT COLUMN CONTENT (The Answer Sheet)
  const answerSheet = (
    <div className="space-y-1">
      {/* Answer Sheet Header */}
      <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-4">
        <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
          Antwortbogen
        </h3>
        <p className="text-xs text-gray-600 mt-1">
          Markieren Sie die richtige Antwort.
        </p>
      </div>

      {/* Examples List (Rendered like questions) */}
      {examples && examples.map((ex, idx) => {
        const exampleText = ex.questionText || ex.statement || ex.question;
        const correctAnswer = ex.correctAnswer;

        return (
          <div
            key={`ex-${idx}`}
            className="border border-gray-900 bg-yellow-50 p-4 rounded-none opacity-80"
          >
            {/* Example Text */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <span className="font-bold text-sm mr-2">
                  0.
                </span>
                <span className="text-sm leading-relaxed">
                  {exampleText}
                </span>
              </div>
            </div>

            {/* Richtig/Falsch Options - Showing the correct answer */}
            <div className="grid grid-cols-2 gap-3">
              {/* Richtig Button */}
              <button
                disabled
                className={`
                  border-2 py-2 px-4 text-sm font-bold transition-all rounded-none text-center cursor-not-allowed
                  ${correctAnswer === 'Richtig'
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-400 bg-white text-gray-900'
                  }
                `}
              >
                Richtig
              </button>
              {/* Falsch Button */}
              <button
                disabled
                className={`
                  border-2 py-2 px-4 text-sm font-bold transition-all rounded-none text-center cursor-not-allowed
                  ${correctAnswer === 'Falsch'
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-400 bg-white text-gray-900'
                  }
                `}
              >
                Falsch
              </button>
            </div>
          </div>
        );
      })}

      {/* Questions List */}
      {questions.map(q => {
        const currentAnswer = partAnswers[q.questionId];

        return (
          <div
            key={q.questionId}
            className="border border-gray-900 bg-white p-4 rounded-none hover:bg-gray-50 transition-colors"
          >
            {/* Question Text with Flag */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <span className="font-bold text-sm mr-2">
                  {q.questionNumber}.
                </span>
                <span className="text-sm leading-relaxed">
                  {q.questionText || q.question}
                </span>
              </div>
              <FlagButton
                questionId={q.questionId}
                flaggedQuestions={flaggedQuestions}
                toggleFlagQuestion={toggleFlagQuestion}
                {...props}
              />
            </div>

            {/* Richtig/Falsch Options */}
            <div className="grid grid-cols-2 gap-3">
              {/* Richtig Button */}
              <button
                onClick={() => handleSingleAnswer(q.questionId, 'Richtig')}
                className={`
                  border-2 py-2 px-4 text-sm font-bold transition-all rounded-none text-center
                  ${currentAnswer === 'Richtig'
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-400 bg-white text-gray-900 hover:border-gray-900 hover:shadow-md hover:-translate-y-0.5'
                  }
                `}
              >
                Richtig
              </button>
              {/* Falsch Button */}
              <button
                onClick={() => handleSingleAnswer(q.questionId, 'Falsch')}
                className={`
                  border-2 py-2 px-4 text-sm font-bold transition-all rounded-none text-center
                  ${currentAnswer === 'Falsch'
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-400 bg-white text-gray-900 hover:border-gray-900 hover:shadow-md hover:-translate-y-0.5'
                  }
                `}
              >
                Falsch
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
          <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{answerSheet}</div>
      </div>
    </div>
  ) : null;

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
      <div className="grid grid-cols-1 lg:grid-cols-10 lg:gap-8">
        <div className="lg:col-span-7 min-w-0">
          {blogContent}
        </div>

        <div className="hidden lg:block lg:col-span-3 mt-6 lg:mt-0">
          <div className="lg:sticky lg:top-6">
            {answerSheet}
          </div>
        </div>
      </div>
      {mobileOverlay}
    </div>
  );
};

export default BlogPostComprehension;